import FileFormats from '../../constants/FileFormats'
import { OrderProduct } from '../../entities/order-product/OrderProduct'
import { api, Api } from '../../services/api'
import { getLabelFormat } from '../../services/label-format/labelFormat'
import { getLabelSize } from '../../services/label-size/labelSize'
import { getPrinterDpi } from '../../services/printer/printerSettings'
import { ILabelDataHandler } from './ILabelDataHandler'

export class LabelApiDataHandler implements ILabelDataHandler {
  api: Api
  _resource = 'sra_order_labels'

  constructor() {
    this.api = api
  }

  /**
   * Resolved per request, not cached in the constructor: this handler is a
   * module-scope singleton (services/store/epics/dashboard/index.ts) that is built
   * once at startup, so caching here would mean a format change from the drawer only
   * took effect after a full app restart.
   */
  get _format(): FileFormats {
    return getLabelFormat()
  }

  async getDocumentBase64ByLabel(orderId: number, label: string, itemId: number): Promise<{fileContent: string, fileType: FileFormats}> {
    const format = this._format
    // size and dpi describe the physical label stock and the printer's resolution, so
    // they only mean anything for ZPL. A PDF goes to the platform print dialog, which
    // picks its own paper. Sending them for PDF would imply a control the seller does
    // not have.
    //
    // dpi is not optional alongside size: 4x6 is 812x1218 dots at 203dpi but 1200x1800
    // at 300dpi, so a size without a resolution renders at the wrong scale rather than
    // failing visibly. It is detected from the printer via ZPL ~HI, not typed in.
    const geometry = format === FileFormats.ZPL
      ? `&size=${encodeURIComponent(getLabelSize())}&dpi=${getPrinterDpi()}`
      : ''

    const url = `${this._resource}/${orderId}?label=${label}&item_ids=${itemId}&format=${format}${geometry}`
    console.log('LabelApiDataHandler: Making API call', { url, orderId, label, itemId, format })

    const response = await this.api.client.get(url)
    const responseData = response.data as any

    console.log('LabelApiDataHandler: API response', {
      status: response.status,
      data: typeof responseData === 'string' ? `HTML/String response (${responseData.length} chars)` : responseData,
      isString: typeof responseData === 'string',
      hasFile: typeof responseData === 'object' && responseData !== null && 'file' in responseData,
      fileType: typeof responseData === 'object' && responseData !== null ? typeof responseData.file : 'N/A',
      fileLength: typeof responseData === 'object' && responseData !== null ? responseData.file?.length : 'N/A',
    })

    // Check if response is HTML error page instead of JSON
    if (typeof responseData === 'string') {
      // Extract error message from HTML if possible
      const errorMatch = responseData.match(/<p[^>]*>([^<]*(?:not found|error|exception)[^<]*)<\/p>/i)
      const errorMessage = errorMatch ? errorMatch[1] : 'Server returned HTML error page instead of JSON'
      throw new Error(`Server Error: ${errorMessage}`)
    }

    const fileContent = responseData.file

    // Deliberately the local, not a second this._format read: the seller can change
    // the format from the drawer while this request is in flight, and re-reading here
    // would tag the response with a format the server was never asked to render.
    return {fileContent, fileType: format}
  }

  create(product: OrderProduct): Promise<string> {
    return this.createBatch([product])
  }

  async createBatch(products: OrderProduct[]): Promise<string> {
    const { data: { label } } = await this.api.client.post<{label: string}>(
      this._resource,
      this._composeCreateRequestData(products)
    )

    return label
  }

  remove(product: OrderProduct): Promise<void> {
    return this.removeBatch([product])
  }

  removeBatch(products: OrderProduct[]): Promise<void> {
    const orderId = this._composeOrderId(products)
    return this.api.client.put(`${this._resource}/${orderId}`, this._composeRemoveRequestData(products))
  }

  _composeOrderId(products: OrderProduct[]): number {
    if (!products.length) {
      throw new Error('No products specified for the request')
    }

    const orderId = products[0].order?.orderId

    if (!orderId) {
      throw new Error(`Orphan product was found: product with id ${products[0].id} has no order`)
    }

    return orderId
  }

  _composeCreateRequestData(products: OrderProduct[]): { order_id: number, item_ids: number[] } {
    return {
      ...this._composeOrderIdRequestData(products),
      ...this._composeItemIdsRequestData(products),
    }
  }

  _composeRemoveRequestData(products: OrderProduct[]): { item_ids: number[] } {
    return {
      ...this._composeItemIdsRequestData(products),
    }
  }

  _composeOrderIdRequestData(products: OrderProduct[]): { order_id: number } {
    return {
      order_id: this._composeOrderId(products),
    }
  }

  _composeItemIdsRequestData(products: OrderProduct[]): { item_ids: number[] } {
    return {
      item_ids: products.reduce<number[]>((itemIds, { itemId }) => ([...itemIds, itemId]), []),
    }
  }
}
